/**
 * Users Management Page
 * ======================
 * User management module with CRUD operations
 */

import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout';
import { Card, CardBody, Button, Alert } from '../components/ui';
import { UserForm, RoleBadge } from '../components/users';
import { userAPI } from '../api';
import { ROLE_LABELS, PAGINATION } from '../utils/constants';
import { useToast } from '../contexts/ToastContext';
import { usePermissions } from '../hooks';
import { useOrg } from '../hooks';

const Users = () => {
  const toast = useToast();
  const { isAdmin } = usePermissions();
  const { organizations, activeOrg, isSuperAdmin } = useOrg();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Filters and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(PAGINATION.DEFAULT_PAGE);
  const [pagination, setPagination] = useState(null);

  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        per_page: PAGINATION.DEFAULT_PER_PAGE,
      };

      if (searchTerm) params.search = searchTerm;
      if (roleFilter) params.role = roleFilter;
      if (statusFilter) params.status = statusFilter;

      const response = await userAPI.getUsers(params);
      setUsers(response.data || []);
      setPagination(response.pagination);
    } catch (err) {
      setError(err);
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, roleFilter, statusFilter]);

  // Handle create user
  const handleCreateUser = () => {
    setModalMode('create');
    setSelectedUser(null);
    setShowModal(true);
  };

  // Handle edit user — fetch full detail so org memberships prefill the form
  const handleEditUser = async (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    setShowModal(true);
    try {
      const detail = await userAPI.getUserById(user.id);
      setSelectedUser(detail);
    } catch (err) {
      console.error('Error loading user detail:', err);
    }
  };

  // Handle form submit
  const handleFormSubmit = async (formData) => {
    try {
      setSubmitting(true);

      if (modalMode === 'create') {
        // userAPI.createUser already returns the extracted data (response.data.data)
        const userData = await userAPI.createUser(formData);

        // Check if password was auto-generated
        if (userData.generated_password) {
          toast.success(
            `User created successfully. Welcome email sent.`,
            { duration: 10000 }
          );
          // Also show an alert for emphasis
          
        } else {
          toast.success('User created successfully');
        }
      } else {
        // userAPI.updateUser already returns the extracted data (response.data.data)
        const userData = await userAPI.updateUser(selectedUser.id, formData);

        // Check if password was changed
        if (userData.password_changed) {
          toast.success('User updated successfully. Password change notification email sent.');
        } else {
          toast.success('User updated successfully');
        }
      }

      setShowModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      // Error interceptor transforms errors to {code, message, details}
      const errorMessage = err.message || 'Failed to save user';
      toast.error(errorMessage);
      console.error('Error saving user:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete user
  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.first_name} ${user.last_name}?`)) {
      return;
    }

    try {
      await userAPI.deleteUser(user.id, 'Deleted by admin');
      toast.success(`User ${user.first_name} ${user.last_name} deleted successfully`);
      fetchUsers();
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete user';
      toast.error(errorMessage);
      console.error('Error deleting user:', err);
    }
  };

  // Handle toggle user status
  const handleToggleStatus = async (user) => {
    try {
      await userAPI.updateUserStatus(user.id, !user.is_active, 'Status changed by admin');
      const statusText = user.is_active ? 'deactivated' : 'activated';
      toast.success(`User ${user.first_name} ${user.last_name} ${statusText} successfully`);
      fetchUsers();
    } catch (err) {
      const errorMessage = err.message || 'Failed to update user status';
      toast.error(errorMessage);
      console.error('Error updating user status:', err);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page
  };

  // Handle filter change
  const handleFilterChange = (filterType, value) => {
    if (filterType === 'role') {
      setRoleFilter(value);
    } else if (filterType === 'status') {
      setStatusFilter(value);
    }
    setCurrentPage(1); // Reset to first page
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-[28px] font-sans font-bold text-gray-900 dark:text-white mb-2">
              User Management
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              Manage admin portal users, roles, and permissions
            </p>
          </div>
          {isAdmin && (
            <Button variant="primary" onClick={handleCreateUser}>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add User
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div>
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary"
                />
              </div>

              {/* Role Filter */}
              <div>
                <select
                  value={roleFilter}
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary"
                >
                  <option value="">All Roles</option>
                  {Object.entries(ROLE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Error State */}
        {error && (
          <div className="mb-6">
            <Alert
              variant={error.code === 'NETWORK_ERROR' ? 'info' : 'error'}
              title={error.code === 'NETWORK_ERROR' ? 'Connection Issue' : 'Failed to Load Users'}
            >
              {error.message || 'An unexpected error occurred.'}
            </Alert>
            <div className="mt-4 text-center">
              <Button onClick={fetchUsers}>Retry</Button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        )}

        {/* Users Table */}
        {!loading && !error && (
          <Card>
            <CardBody className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Roles
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Department
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Login
                      </th>
                      {isAdmin && (
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                          No users found
                        </td>
                      </tr>
                    ) : (
                      users.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 bg-gradient-primary rounded-full flex items-center justify-center text-white font-medium">
                                {user.first_name?.[0]}{user.last_name?.[0]}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {user.first_name} {user.last_name}
                                </div>
                                <div className="text-sm text-gray-500">{user.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1">
                              {user.roles?.map((role) => (
                                <RoleBadge
                                  key={role}
                                  role={role}
                                  isPrimary={role === user.primary_role}
                                  size="sm"
                                />
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {user.department || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                              user.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                          </td>
                          {isAdmin && (
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleEditUser(user)}
                                  className="text-primary hover:text-primary-600"
                                  title="Edit user"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleToggleStatus(user)}
                                  className="text-gray-600 hover:text-gray-900"
                                  title={user.is_active ? 'Deactivate user' : 'Activate user'}
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={user.is_active ? "M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" : "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"} />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(user)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete user"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination && pagination.total_pages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing page {pagination.page} of {pagination.total_pages} ({pagination.total} total users)
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={!pagination.has_prev}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={!pagination.has_next}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {/* User Form Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-strong max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-2xl font-sans font-semibold text-gray-900 dark:text-white">
                  {modalMode === 'create' ? 'Create New User' : 'Edit User'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6">
                <UserForm
                  user={selectedUser}
                  onSubmit={handleFormSubmit}
                  onCancel={() => setShowModal(false)}
                  loading={submitting}
                  organizations={organizations}
                  defaultOrgId={activeOrg?.id}
                  isSuperAdmin={isSuperAdmin}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Users;
